import { differenceInDays } from 'date-fns';
import { isEmpty } from 'lodash-es';
import * as urlTemplate from 'url-template';
import { TranslocoService } from '@jsverse/transloco';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';

import { isNullOrUndefined } from '@app/util/util';
import {
  mapPriceToRates
} from '@autos/auto-results/components/auto-detail-rates/mappers/rates-mapper';
import { mapAutoCarInfo } from '@autos/auto-results/mappers/auto-info.mapper';
import { DateTransformPipe } from '@app/pipes/date-format.pipe';
import { RateUI } from '@autos/auto-results/components/auto-detail-rates/entities/rates';
import { AutoInfo } from '@autos/auto-results/entities/auto-info';
import { autoApi } from '@autos/auto.api';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';

@Component({
  selector: 'app-auto-reservation',
  templateUrl: './auto-reservation.component.html',
  styleUrls: ['./auto-reservation.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AutoReservationComponent {

  @Input() autoValidation: VehicleValidateResponse;
  @Input() conversionRate: number;

  private readonly DATE_FORMAT: string = 'E, d MMM yyyy';
  private readonly TIME_FORMAT: string = 'HH:mm';

  constructor(
    public translateService: TranslocoService,
    private datePipe: DatePipe,
    private dateTransformPipe: DateTransformPipe
  ) { }

  get model(): string {
    if (!isNullOrUndefined(this.autoValidation.VehicleOption?.Vehicle?.Model)) {
      return this.autoValidation.VehicleOption.Vehicle.Model;
    }
  }

  get pickUp(): string {
    if (!isNullOrUndefined(this.autoValidation.VehicleOption?.PickUp?.Time)) {
      return  this.formatDate(new Date(this.autoValidation.VehicleOption.PickUp.Time));
    }
  }

  get pickUpTime(): string {
    if (!isNullOrUndefined(this.autoValidation.VehicleOption?.PickUp?.Time)) {
      return this.datePipe.transform(new Date(this.autoValidation.VehicleOption.PickUp.Time), this.TIME_FORMAT);
    }
  }

  get dropOff(): string {
    if (!isNullOrUndefined(this.autoValidation.VehicleOption?.DropOff?.Time)) {
      return this.formatDate(new Date(this.autoValidation.VehicleOption.DropOff.Time));
    }
  }

  get dropOffTime(): string {
    if (!isNullOrUndefined(this.autoValidation.VehicleOption?.DropOff?.Time)) {
      return this.datePipe.transform(new Date(this.autoValidation.VehicleOption.DropOff.Time), this.TIME_FORMAT);
    }
  }

  get pickUpPlace(): string {
    const place = this.autoValidation.VehicleOption?.PickUp?.Address;
    return !isNullOrUndefined(place) && !isEmpty(place) ? place : '';
  }

  get dropOffPlace(): string {
    const place = this.autoValidation.VehicleOption?.DropOff?.Address;
    return !isNullOrUndefined(place) && !isEmpty(place) ? place : '';
  }

  get totalPricePayNow(): number {
    return (this.autoValidation.VehicleOption?.Rates[0].TaxesPayableAtDestination) ?
      this.autoValidation.VehicleOption.Rates[0].TotalPricePayNow : null;
  }

  get info(): AutoInfo {
    return (this.autoValidation.VehicleOption) ? mapAutoCarInfo(this.autoValidation.VehicleOption) : {} as AutoInfo;
  }

  get hasRateDescription(): boolean {
    return !!this.info?.rateDescription?.trim();
  }

  get contract(): string {
    return (this.autoValidation.VehicleOption?.CommentContracts) ? this.autoValidation.VehicleOption?.CommentContracts : null;
  }

  get period(): string {
    const vehicle = this.autoValidation.VehicleOption;
    if (vehicle) {
      const diff = differenceInDays(new Date(vehicle.DropOff.Time), new Date(vehicle.PickUp.Time));
      return (diff > 1) ?
        this.translateService.translate('AUTOS_MODULE.RATES.DAYS_RENT', { period: diff }) :
        this.translateService.translate('AUTOS_MODULE.RATES.ONEDAY_RENT');
    }
    return '';
  }

  get currency(): string {
    const vehicle = this.autoValidation.VehicleOption;
    if (vehicle && vehicle.Rates && vehicle.Rates.length > 0 && vehicle.Rates[0].Price) {
      return vehicle.Rates[0].Price.CurrencyCode;
    }
    return '';
  }

  get rates(): RateUI[] {
    const vehicle = this.autoValidation.VehicleOption;
    if (vehicle && vehicle.Rates && vehicle.Rates.length > 0 && vehicle.Rates[0].Price) {
      return mapPriceToRates(vehicle.Rates[0].Price, this.payAtDestination, this.totalPricePayNow);
    }
    return [];
  }

  get payAtDestination(): number {
    return this.autoValidation?.VehicleOption?.Rates?.length &&
      this.autoValidation?.VehicleOption?.Rates[0]?.TaxesPayableAtDestination &&
      this.autoValidation?.VehicleOption?.Rates[0]?.Price?.Taxes ? 
      this.autoValidation.VehicleOption.Rates[0].Price.Taxes as number : 0;
  }

  private formatDate(date: Date): string {
    return this.formatStringDate(this.datePipe.transform(date, this.DATE_FORMAT));
  }

  private formatStringDate(date: string): string {
    return this.dateTransformPipe.transform(date);
  }

  public getLogo(): string {
    const url = urlTemplate.parse(autoApi.auto_images).expand({
      provider: `${this.autoValidation.VehicleOption?.Company?.Code?.toUpperCase()}`
    });
    return (this.autoValidation.VehicleOption?.Company) ? url : '';
  }

  public getImage(): string {
    return (this.autoValidation.VehicleOption?.Vehicle?.Image) ? this.autoValidation.VehicleOption.Vehicle.Image : '';
  }

  public getBackgroundImage(type: string): { backgroundImage: string } | string {
    const url = (type === 'logo') ? this.getLogo() : this.getImage();
    if (url !== '') return { backgroundImage: `url(${encodeURI(url)})` };
    return '';
  }
}
